import React, { useEffect, useMemo, useRef, useState } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { init } from '@noriginmedia/norigin-spatial-navigation';
import { OptimizelyProvider } from '@optimizely/react-sdk';

import { useDeviceWake } from '@/hooks/useDeviceWake';
import { NAVIGATION_END_EVENT, NAVIGATION_START_EVENT } from '@/hooks/usePageState';
import { usePlayerPreload } from '@/hooks/usePlayerPreload';
import { useSessionScopedAppQuery } from '@/hooks/useSessionScopedAppQuery';
import { useTitle } from '@/hooks/useTitle';

import { TTS } from '@/components/Accessibility/speechSynthesizer';
import AppConfig from '@/components/AppConfig';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { useGlobalConfig } from '@/components/GlobalContext';
import Screen from '@/components/Screen';
import ScreenSaver from '@/components/ScreenSaver';
import { useAssertSpinnerProvider, useSpinner } from '@/components/SpinnerContext';

import { useConfig } from '@/appconfig/ConfigContext';
import { CraveErrorCode } from '@/enums/errorCodes';
import { IntentAction } from '@/enums/intentAction';
import {
  SignOutReason,
  init as initAppMonitoringSuite,
  log,
} from '@/services/appMonitoringSuite';
import { getAccessToken, resolveAutoSelectedProfile } from '@/services/auth';
import { useAuth } from '@/services/auth/authContext';
import { initComscore } from '@/services/comscore';
import { pageView } from '@/services/customerDataSuite';
import {
  identifyUser,
  init as initCustomerDataSuite,
} from '@/services/customerDataSuite';
import {
  ScreenName,
  publicRoutes,
  screenMapping,
} from '@/services/customerDataSuite/constants';
import { optimizelyClient } from '@/services/optimizely';
import { init as initUserEngagementSuite } from '@/services/userEngagementSuite';
import { useSmartId } from '@/services/userEngagementSuite/SmartIdContext';
import { getSystem, isXfinitySystem } from '@/system';
import { deeplinkStorage } from '@/utils/deeplinkStorage';
import { getCraveError } from '@/utils/errors';
import { getScreenViewAttributes } from '@/utils/getScreenViewAttributes';
import { getLocalization } from '@/utils/localization';

import QueryDevtools from './QueryDevtools';
import { AppProvider } from './frontdoor/AppContext';

type AppProps = {
  children: React.ReactNode;
};

const system = getSystem();

const App: React.FC<AppProps> = (props) => {
  const router = useRouter();
  const config = useConfig();
  const {
    selectedProfile,
    profiles,
    getProfiles,
    logout,
    checkUserProfiles,
    fatalErrorCode,
    clearFatalError,
    isAuthenticated,
  } = useAuth();
  const { children } = props;
  const showAppconfig: Boolean = false;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const screenId = searchParams?.get('id');
  const smartIdRef = useRef<string | null>('');
  const { smartId } = useSmartId();
  const { title } = useTitle();
  const lastTitleRef = useRef<string | null>(null);
  const [isOffline, setIsOffline] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const { userLanguage } = useGlobalConfig();

  const { app, loading, error, refetch } = useSessionScopedAppQuery();

  const lastUrlRef = useRef<string | null>(null);
  const userInfo = {
    id: selectedProfile?.accountId ?? 'anonymous',
  };

  useEffect(() => {
    system.init();
    if (system.overrideAria) TTS.init();

    try {
      const cleanup = system.monitorConnectionStatus((isOnline: boolean) => {
        setIsOffline(!isOnline);
      });

      return () => {
        cleanup?.();
      };
    } catch (error) {
      setIsOffline(false);
    }
  }, []);

  useEffect(() => {
    smartIdRef.current = smartId;
  }, [smartId]);

  usePlayerPreload(!!selectedProfile, pathname);

  useEffect(() => {
    if (config) {
      initCustomerDataSuite(config);
      initAppMonitoringSuite(config);
      initUserEngagementSuite();
      const isComscoreEnabled = config?.featureFlags?.comScore ?? false;
      isComscoreEnabled && initComscore(config);
    }
  }, [config]);

  const screenTitle = useMemo(() => {
    const matchedKey = Object.keys(screenMapping).find((key) => pathname?.includes(key));
    return matchedKey ? screenMapping[matchedKey] : ScreenName.UNKNOWN;
  }, [screenId, pathname]);

  useEffect(() => {
    if (!title || lastTitleRef.current === title) return;

    const attributes = getScreenViewAttributes(
      selectedProfile,
      screenTitle,
      title ?? '',
      pathname ?? '',
      searchParams ?? new URLSearchParams(),
      smartIdRef.current ?? ''
    );

    pageView('screen_viewed', attributes, { search: searchParams?.toString() ?? '' });
    lastTitleRef.current = title;
    lastUrlRef.current = window.location.toString();
  }, [title]);

  useEffect(() => {
    const handleDeepLink = async (
      contentId: string,
      action?: IntentAction,
      data?: { query?: string; sectionName?: string }
    ) => {
      if (isXfinitySystem(getSystem())) {
        const params = new URLSearchParams();
        if (contentId) params.append('contentId', encodeURIComponent(contentId));

        if (action) {
          params.append('action', encodeURIComponent(action));
        }
        if (data?.query) {
          params.append('query', encodeURIComponent(data.query));
        }
        if (data?.sectionName) {
          params.append('sectionName', encodeURIComponent(data.sectionName));
        }
        router.push(`/deep-link?${params.toString()}`);
      } else {
        await getProfiles();
        router.push(`/deep-link?contentId=${contentId}`);
      }
    };

    const deepLinkCleanup = system.initDeepLink?.(handleDeepLink);
    return () => {
      deepLinkCleanup?.();
    };
  }, []);

  const onNavigate = () => {
    setIsNavigating(true);
  };

  const onNavigateEnd = () => {
    setIsNavigating(false);
  };

  useEffect(() => {
    window.addEventListener(NAVIGATION_START_EVENT, onNavigate);
    window.addEventListener(NAVIGATION_END_EVENT, onNavigateEnd);

    return () => {
      window.removeEventListener(NAVIGATION_START_EVENT, onNavigate);
      window.removeEventListener(NAVIGATION_END_EVENT, onNavigateEnd);
    };
  }, []);

  useDeviceWake(async () => {
    const accessToken = await getAccessToken();

    if (accessToken) {
      try {
        const profiles = await checkUserProfiles(accessToken);
        if (!profiles || profiles.length === 0) throw new Error('No Profiles found');
        identifyUser(profiles[0].accountId);
      } catch (e) {
        log('DeviceWakeUp on App - Unable to Refresh Token', 'error');
        redirectToFrontdoor();
      }
    }
  });

  // logout() clears the Apollo store (flushCache -> clearStore), which cancels this no-cache
  // app query if it was still in flight. It also bumps sessionId, so useSessionScopedAppQuery
  // refetches for the anonymous session; nothing to recover from here.
  const redirectToFrontdoor = async () => {
    try {
      log('App redirecting to frontdoor. Token Missing or expired logout', 'warn');
      await logout(SignOutReason.TOKENMISSING);
      router.push('/frontdoor');
    } catch (e) {
      log('App redirecting to frontdoor. Failed to remove token.', 'error');
      router.push('/frontdoor');
    }
  };

  // Only SplashScreen ('/') checks auth on load; any other route (e.g. a stale
  // resumed URL) would otherwise render unauthenticated. Deep links are exempt
  // so they keep working for signed-out users.
  // If a user signs out, the isAuthenticated dep will change, firing this effect to run and determin the users entitled routes.
  const isPublicRoute =
    !pathname ||
    pathname === '/' ||
    publicRoutes.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
    );

  // The profile picker and PIN screen are where a session's profile gets chosen,
  // so the guard must never bounce them back into that same flow.
  const isProfileRoute = pathname?.startsWith('/profile') ?? false;

  // The guard below is async, so a protected screen would otherwise paint (and
  // fire its own queries) before we know whether to bounce the user. Hold it
  // Gate to prevent unauthed users from viewing (even breifly) screens which are protected.
  // Keep splash visible until we determine auth state, profile state, and proper route.
  const [hasClearedEntry, setHasClearedEntry] = useState(false);
  const showEntryLoader =
    !isPublicRoute &&
    !isProfileRoute &&
    (!isAuthenticated || (!hasClearedEntry && !selectedProfile));

  // Every spinner in the app is requested through a provider that lives above
  // this component. Losing it is otherwise invisible - requests just stop
  // resolving to anything - so fail here instead.
  useAssertSpinnerProvider();

  // The two app-level loading states. Both go through the same registry as
  // every page-level one, so they can never stack.
  const hasError = isOffline || !!fatalErrorCode;
  useSpinner(showEntryLoader && !hasError, 'app:entry-gate');
  useSpinner(isNavigating && !hasError, 'app:navigation');

  useEffect(() => {
    if (isPublicRoute) return;
    if (deeplinkStorage.isProcessing() || system?.getDeepLinkId?.()) {
      setHasClearedEntry(true);
      return;
    }

    const guardUnauthenticatedEntry = async () => {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        log(
          'App: unauthenticated entry with no deep link - redirecting to frontdoor',
          'warn'
        );
        // Stay behind the loader: the redirect lands on a public route, which
        // drops the gate on its own.
        redirectToFrontdoor();
        return;
      }

      const currentProfiles = profiles.length > 0 ? profiles : await getProfiles();

      if (currentProfiles.length === 0) {
        log('App: entry guard found no profiles - redirecting to frontdoor', 'error');
        redirectToFrontdoor();
        return;
      }

      if (!selectedProfile && !isProfileRoute) {
        const autoSelectedProfile = resolveAutoSelectedProfile(currentProfiles);

        if (!autoSelectedProfile || autoSelectedProfile.hasPin) {
          const query = searchParams?.toString();
          const returnUrl = `${pathname}${query ? `?${query}` : ''}`;
          log(
            `App: entry guard has no selectable profile - prompting for ${autoSelectedProfile ? 'PIN' : 'profile selection'}`,
            'info'
          );
          router.replace(
            autoSelectedProfile
              ? `/profile/enter-pin?id=${autoSelectedProfile.id}&returnUrl=${encodeURIComponent(returnUrl)}`
              : `/profile?postLoginRedirect=${encodeURIComponent(returnUrl)}`
          );
          return;
        }
      }
      setHasClearedEntry(true);
    };

    guardUnauthenticatedEntry().catch((error) => {
      log(
        `App: unauthenticated entry guard failed. Redirect to front door. - ${error}`,
        'error'
      );
      redirectToFrontdoor();
    });
  }, [isAuthenticated, pathname]);

  if (isOffline) {
    const noInternetConnectionError = getCraveError(
      CraveErrorCode.NO_NETWORK_CONNECTION_4001,
      userLanguage
    );

    return (
      <Screen>
        <div className="h-screen overflow-hidden">
          <ErrorOverlay error={noInternetConnectionError} />
        </div>
      </Screen>
    );
  }

  if (fatalErrorCode) {
    const fatalError = getCraveError(fatalErrorCode, userLanguage);
    const fatalButtonLabel = fatalError.retry
      ? getLocalization(userLanguage)['errors']['error-button-retry']
      : getLocalization(userLanguage)['errors']['error-button-ok'];
    // Reload from home: clears the error, retries the token refresh, and exits the error screen.
    const onFatalErrorButton = () => {
      clearFatalError();
      redirectToFrontdoor();
    };
    return (
      <Screen>
        <div className="h-screen overflow-hidden">
          <ErrorOverlay
            error={fatalError}
            buttonLabel={fatalButtonLabel}
            onKeyPress={onFatalErrorButton}
          />
        </div>
      </Screen>
    );
  }

  return (
    <OptimizelyProvider optimizely={optimizelyClient} user={userInfo}>
      <AppProvider app={app} loading={loading} error={error} refetch={refetch}>
        <Screen>
          <div className="h-screen overflow-hidden">
            {/* The gate withholds children rather than covering them: a protected
                screen must not mount and fire its queries before auth is known.
                The spinner itself comes from SpinnerProvider.
                Fragment is load-bearing: RSC-built children aren't key-validated, so listing them bare warns. */}
            {showEntryLoader ? null : <>{children}</>}
            {showAppconfig ? <AppConfig /> : null}
            <ScreenSaver />
          </div>
          <QueryDevtools />
        </Screen>
      </AppProvider>
    </OptimizelyProvider>
  );
};

function startUpApp() {
  init({
    shouldFocusDOMNode: false,
    useGetBoundingClientRect: true,
    throttleKeypresses: true,
    throttle: system.keyThrottleDelay,
  });
}

startUpApp();

export default App;
